/**
 * EST3 Wave 8 — Original Project Cost Budget handoff panel.
 * Prefers deep-link into Project Cost Budget workbench when project/award known.
 */
import React, { useState } from 'react';
import { Alert, Button, Card, Descriptions, Input, Modal, Space, Tag, Typography, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { formatMoney } from '@/utils/format';
import { projectService } from '@/services/project.service';
import { epcEstimationService } from '@/services/epcEstimation.service';

const { Text } = Typography;

export const EpcBudgetHandoffPanel: React.FC<{
  estimateId?: string;
  revisionId?: string;
  defaultProjectId?: string;
  defaultAwardId?: string;
}> = ({ estimateId, revisionId, defaultProjectId, defaultAwardId }) => {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [projectId, setProjectId] = useState(defaultProjectId || '');
  const [awardId, setAwardId] = useState(defaultAwardId || '');

  const previewQ = useQuery({
    queryKey: ['epc-est3-budget-preview', projectId, awardId],
    queryFn: () => projectService.getEst3BudgetPreview(projectId, awardId),
    enabled: !!projectId && !!awardId,
  });

  const lineageQ = useQuery({
    queryKey: ['epc-est3-budget-lineage', projectId],
    queryFn: () => projectService.getEst3BudgetLineage(projectId),
    enabled: !!projectId,
  });

  const mapM = useMutation({
    mutationFn: () =>
      epcEstimationService.autoMapWbs(estimateId!, revisionId!, projectId),
    onSuccess: (r: any) => {
      message.success(`Mapped ${r?.mapped || 0}/${r?.total || 0} WBS by code`);
      qc.invalidateQueries({ queryKey: ['epc-est3-budget-preview'] });
    },
    onError: (e: Error) => message.error(e.message || 'Map failed'),
  });

  const convertM = useMutation({
    mutationFn: () =>
      projectService.convertBudgetFromEst3(projectId, {
        awardId,
        previewHash: String((previewQ.data as any)?.preview_hash || ''),
      }),
    onSuccess: (r: any) => {
      message.success(r?.idempotent ? 'Original Budget already exists' : 'Original Budget generated');
      qc.invalidateQueries({ queryKey: ['epc-est3-budget-lineage', projectId] });
      qc.invalidateQueries({ queryKey: ['epc-est3-budget-preview'] });
    },
    onError: (e: Error) => message.error(e.message || 'Convert failed'),
  });

  const p = previewQ.data as Record<string, any> | undefined;
  const lin = lineageQ.data as Record<string, any> | undefined;

  const openWorkbench = () => {
    if (!projectId) {
      message.warning('Enter Project ID first');
      return;
    }
    const qs = awardId ? `?awardId=${encodeURIComponent(awardId)}` : '';
    navigate(`/prj/projects/${projectId}/budgets${qs}`);
  };

  return (
    <Card size="small" title="Execution handoff — Original Cost Budget">
      <Space direction="vertical" style={{ width: '100%' }} size="small">
        <Text type="secondary">
          Cost only from Award Basis Total Estimated Cost — never Award Value / Final Bid.
        </Text>
        <Space wrap>
          <Input
            placeholder="Project ID"
            value={projectId}
            onChange={(e) => setProjectId(e.target.value.trim())}
            style={{ width: 220 }}
          />
          <Input
            placeholder="Award ID"
            value={awardId}
            onChange={(e) => setAwardId(e.target.value.trim())}
            style={{ width: 220 }}
          />
          <Button type="primary" ghost disabled={!projectId} onClick={openWorkbench}>
            Open Budget workbench
          </Button>
          <Button
            disabled={!projectId || !awardId}
            onClick={() => previewQ.refetch()}
            loading={previewQ.isFetching}
          >
            Preview
          </Button>
          <Button
            disabled={!projectId || !estimateId || !revisionId}
            loading={mapM.isPending}
            onClick={() => mapM.mutate()}
          >
            Auto-map WBS by code
          </Button>
        </Space>

        {lin?.conversion && (
          <Alert
            type="success"
            showIcon
            message={`Original Budget AED ${lin.original_budget_total} · source ${lin.source_estimate_revision_id}`}
            action={
              <Button size="small" onClick={openWorkbench}>
                View in workbench
              </Button>
            }
          />
        )}

        {p && (
          <>
            <Space wrap>
              <Tag color={p.status === 'READY' ? 'green' : 'red'}>{p.status}</Tag>
              <Tag>
                WBS {p.wbs_mapping?.mapped}/{p.wbs_mapping?.total}
              </Tag>
              <Tag>
                Rows {p.row_count}
              </Tag>
            </Space>
            <Descriptions size="small" column={2} bordered>
              <Descriptions.Item label="Estimate cost">
                {formatMoney(Number(p.source_total || 0))}
              </Descriptions.Item>
              <Descriptions.Item label="Mapped">
                {formatMoney(Number(p.mapped_total || 0))}
              </Descriptions.Item>
              <Descriptions.Item label="Unmapped">
                {formatMoney(Number(p.unmapped_total || 0))}
              </Descriptions.Item>
              <Descriptions.Item label="Award value (revenue)">
                {p.revenue_side?.award_value != null
                  ? formatMoney(Number(p.revenue_side.award_value))
                  : '—'}
              </Descriptions.Item>
            </Descriptions>
            {(p.blockers || []).length > 0 && (
              <Alert
                type="error"
                showIcon
                message="Blockers"
                description={(p.blockers as string[]).join(' · ')}
              />
            )}
            <Button
              type="primary"
              disabled={p.status !== 'READY' || !!lin?.conversion}
              loading={convertM.isPending}
              onClick={() => {
                Modal.confirm({
                  title: 'Generate Original Project Cost Budget?',
                  content: `Source cost ${formatMoney(Number(p.source_total))} → ${p.row_count} rows. Idempotent; locks Award Basis.`,
                  onOk: () => convertM.mutateAsync(),
                });
              }}
            >
              Generate Original Budget
            </Button>
          </>
        )}
      </Space>
    </Card>
  );
};
